import clsx from 'clsx';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import type { BaseToolWire } from '../lib/baseToolSchema';

interface BaseToolSummaryProps {
  tool: BaseToolWire | null;
  onEdit: () => void;
  onRemove: () => void;
  disabled?: boolean;
}

/** The base tool of the run, in the configuration column: what is set, and the way to edit it. */
export function BaseToolSummary({
  tool,
  onEdit,
  onRemove,
  disabled = false,
}: BaseToolSummaryProps) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="base-tool-summary"
      className={clsx(
        'mt-1 flex flex-col gap-2 rounded-lg border p-3',
        tool ? 'border-primary/40 bg-accent' : 'border-dashed border-border'
      )}
    >
      <div>
        <p className="text-sm font-semibold text-foreground">
          {tool
            ? t('toolRecommender.baseTool.summary.line', {
                d1: tool.d1,
                teeth: tool['Nb dents'],
                helix: tool['hélice 1'],
                substrate: tool['Substrat / matière outil'],
              })
            : t('toolRecommender.baseTool.summary.none')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t(
            tool
              ? 'toolRecommender.baseTool.summary.setHint'
              : 'toolRecommender.baseTool.summary.noneHint'
          )}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onEdit} disabled={disabled}>
          {t(
            tool
              ? 'toolRecommender.baseTool.summary.edit'
              : 'toolRecommender.baseTool.summary.define'
          )}
        </Button>
        {tool && (
          <Button type="button" variant="ghost" size="sm" onClick={onRemove} disabled={disabled}>
            {t('toolRecommender.baseTool.summary.remove')}
          </Button>
        )}
      </div>
    </div>
  );
}
